import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import '../assets/guardianPlacement.css';
import Loader from '../components/Loader';

const GuardianPlacementStep1 = () => {
  const { propertyId, guardianId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data state
  const [property, setProperty] = useState(null);
  const [guardian, setGuardian] = useState(null);
  const [rooms, setRooms] = useState([]);

  // Mock data for when API fails
  const getMockData = () => {
    const mockProperty = {
      id: parseInt(propertyId),
      property_id: "PROP001",
      name: "12 Savage Road",
      status_text: "Active",
      address_1: "12 Savage Road",
      postcode: "PL5 1BP"
    };

    const mockGuardian = {
      id: parseInt(guardianId),
      unique_guardian_number: "UGD001",
      full_name: "John Smith",
      status: "Documents Required",
      user: {
        mobile_number: "07123456789",
        email: "john.smith@example.com"
      }
    };

    const mockRooms = [
      {
        id: 1,
        name: "Room 101",
        price: 500.00,
        key_number: 2,
        is_couple: false,
        isEmpty: () => true,
        getRoomFirstMonthRentAttribute: () => 500.00
      },
      {
        id: 2,
        name: "Room 102",
        price: 600.00,
        key_number: 1,
        is_couple: true,
        isEmpty: () => true,
        getRoomFirstMonthRentAttribute: () => 600.00
      },
      {
        id: 3,
        name: "Room 103",
        price: 700.00,
        key_number: 2,
        is_couple: false,
        isEmpty: () => false,
        getRoomFirstMonthRentAttribute: () => 700.00
      }
    ];

    return { mockProperty, mockGuardian, mockRooms };
  };

  useEffect(() => {
    loadStep1Data();
  }, [propertyId, guardianId]);

  const loadStep1Data = async () => {
    try {
      setLoading(true);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/guardian-placement/step1/${propertyId}/${guardianId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('API authentication failed, using mock data');
          const { mockProperty, mockGuardian, mockRooms } = getMockData();
          setProperty(mockProperty);
          setGuardian(mockGuardian);
          setRooms(mockRooms);
          setLoading(false);
          return;
        }
        
        // Handle 403 Forbidden - Role access denied
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || 'You don\'t have permission to access this page.';
          setError(errorMessage);
          toast.error(errorMessage);
          setLoading(false);
          return;
        }
        
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setProperty(data.property);
      setGuardian(data.guardian);
      
      // Transform rooms to add required functions (isEmpty, getRoomFirstMonthRentAttribute)
      // JSON doesn't preserve functions, so we need to add them here
      const transformedRooms = (data.rooms || []).map(room => ({
        ...room,
        // isEmpty function - room is empty if status_id is 0 (VACANT)
        isEmpty: () => room.status_id === 0 || room.status_id === null,
        // getRoomFirstMonthRentAttribute function
        getRoomFirstMonthRentAttribute: () => room.price || 0,
      }));
      
      setRooms(transformedRooms);
      setLoading(false);
    } catch (err) {
      console.error('Error loading step 1 data:', err);
      // Use mock data as fallback
      const { mockProperty, mockGuardian, mockRooms } = getMockData();
      setProperty(mockProperty);
      setGuardian(mockGuardian);
      setRooms(mockRooms);
      setLoading(false);
    }
  };

  const handleSingleGuardianAssignment = (roomId) => {
    navigate(`/guardian-placement/step2/${propertyId}/${guardianId}/${roomId}`);
  };

  const handleCoupleAssignment = (roomId) => {
    navigate(`/guardian-placement/step1b/${propertyId}/${guardianId}/${roomId}`);
  };

  if (loading) return <Loader />;
  
  // Display role access error prominently
  if (error) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger" style={{ marginTop: '20px', padding: '20px' }}>
          <h4 style={{ marginTop: 0, marginBottom: '15px' }}>
            <i className="glyphicon glyphicon-exclamation-sign" style={{ marginRight: '10px' }}></i>
            Access Denied
          </h4>
          <p style={{ fontSize: '16px', marginBottom: '10px' }}>{error}</p>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '20px' }}>
            This page is only accessible to users with the following roles:
            <ul style={{ marginTop: '10px', marginBottom: 0 }}>
              <li>SALES WITH GUARDIANS</li>
              <li>GUARDIAN-MANAGER</li>
              <li>GUARDIAN-MANAGER-WITH-MAINTENANCE</li>
            </ul>
          </p>
          <button 
            className="btn btn-primary" 
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }
  
  if (!property || !guardian) return <div className="container py-5 text-danger">Property or Guardian not found</div>;

  return (
    <div className="letting-agent-dashboard">
      {/* Header Section */}
      <section className="letting-agent__header" style={{ backgroundColor: '#12274e' }}>
        <div className="container">
          <div className="row js-property-viewing-of-user">
            <div className="col-md-12" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
              <div className="wrapper">
                <h2 className="col-white">Placing Guardian - Step 1</h2>
                <h4 className="col-white">Assigning {guardian.full_name} to {property.name}</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ marginTop: '20px' }}>
        <div className="container">
          <div className="panel panel-default panel-standard">
            <div className="panel-body">
              <div className="container mx-auto py-8">
                <h3 style={{ color: '#12274e' }}>Select the room:</h3>
                <h5 className="col-dark">Property Name: {property.name}</h5>
                <h5 className="col-dark" style={{ marginBottom: '40px' }}>Guardian Name: {guardian.full_name}</h5>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {rooms.map(room => {
                    if (room.isEmpty && room.isEmpty()) {
                      return (
                        <div key={room.id} className="col-xs-12 col-sm-6 col-md-3">
                          <div className="room-information room-vacant" style={{ marginBottom: '20px' }}>
                            <p className="room-number">Room {room.name}</p>
                            
                            <div style={{ display: 'none' }} id={`room-id-${room.id}`}>{room.id}</div>
                            
                            <span className="status">VACANT</span>
                            
                            <ul className="room-information-in" id={`room-information-in-${room.id}`}>
                              <li>
                                <span style={{ width: '50%' }}>Monthly License fee:</span> £{room.price}
                              </li>
                              <li><span style={{ width: '50%' }}>Security Deposit:</span> £{room.price}</li>
                              <li><span style={{ width: '50%' }}>First Month Rent:</span> £{room.getRoomFirstMonthRentAttribute()}</li>
                              <li><span style={{ width: '50%' }}>Keys:</span> {room.key_number}</li>
                              <li><span style={{ width: '50%' }}>Couple Room:</span> {room.is_couple ? 'Yes' : 'No'}</li>
                              
                              <li className="">
                                <div className="text-center" style={{ marginTop: '10px' }}>
                                  <button
                                    onClick={() => handleSingleGuardianAssignment(room.id)}
                                    className="btn btn-success btn-sm"
                                  >
                                    Assign Single Guardian
                                  </button>
                                  {room.is_couple && (
                                    <button
                                      onClick={() => handleCoupleAssignment(room.id)}
                                      className="btn btn-primary btn-sm"
                                      style={{ marginTop: '10px', display: 'block', width: '100%' }}
                                    >
                                      Assign Couple
                                    </button>
                                  )}
                                </div>
                              </li>
                            </ul>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuardianPlacementStep1;












